import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useFacebookSDK } from '../hooks/useFacebookSDK';

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [signupConfig, setSignupConfig] = useState({
    loading: true,
    configured: false,
    app_id: '',
    configuration_id: '',
    graph_api_version: 'v26.0',
    recovery_configured: false,
    missing_environment_variables: [],
    error: '',
  });
  const { sdkReady, sdkError, launchEmbeddedSignup } = useFacebookSDK({
    appId: signupConfig.app_id,
    configurationId: signupConfig.configuration_id,
    graphApiVersion: signupConfig.graph_api_version,
  });

  // Connection mode: null | 'simple' | 'advanced'
  const [connectMode, setConnectMode] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [quickConnectEnabled, setQuickConnectEnabled] = useState(false);

  const [tags, setTags] = useState([]);
  const [tagForm, setTagForm] = useState({ name: '', color: '#6366f1', description: '' });
  const [editingTag, setEditingTag] = useState(null);

  const [wabaIdInput, setWabaIdInput] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifiedWaba, setVerifiedWaba] = useState(null); // { waba_id, waba_name, phones[] }
  const [savingWaba, setSavingWaba] = useState(false);
  // legacy wabaForm kept for edit-while-connected
  const [wabaForm, setWabaForm] = useState({ waba_id: '', phone_number_id: '', business_name: '', phone_number: '' });

  const [emailForm, setEmailForm] = useState({
    smtp_host: 'smtp.gmail.com', smtp_port: 587,
    smtp_user: '', smtp_pass: '', from_name: '', from_address: '',
    dkim_enabled: false, dkim_domain_name: '', dkim_key_selector: '', dkim_private_key: '',
  });
  const [savingEmail, setSavingEmail] = useState(false);
  const [dnsResults, setDnsResults] = useState(null);
  const [verifyingDns, setVerifyingDns] = useState(false);

  const wa = user?.wa_config || {};
  const isConnected = wa.connected;
  const tokenSaved  = wa.token_saved && !wa.connected;
  const quickConnectAvailable = quickConnectEnabled && signupConfig.configured;

  useEffect(() => {
    api.get('/tags').then(r => setTags(r.data));
    api.get('/auth/config')
      .then(r => setQuickConnectEnabled(r.data.quick_connect_enabled !== false))
      .catch(() => setQuickConnectEnabled(false));
    api.get('/facebook/embedded-signup/config')
      .then(r => setSignupConfig({ loading: false, ...r.data }))
      .catch(() => setSignupConfig(c => ({
        ...c,
        loading: false,
        configured: false,
        error: 'The server could not load the Embedded Signup configuration.',
      })));
    if (user?.email_config) setEmailForm(c => ({ ...c, ...user.email_config }));
  }, [user]);

  // Handle OAuth redirect result (advanced flow callback)
  useEffect(() => {
    if (searchParams.get('wa_connected')) {
      toast.success('WhatsApp Business account connected!');
      refreshUser(); setSearchParams({});
    }
    if (searchParams.get('wa_token_saved')) {
      toast('Facebook authorized! Enter your WABA details below.', { icon: 'ℹ️', duration: 5000 });
      setConnectMode('advanced');
      refreshUser(); setSearchParams({});
    }
    if (searchParams.get('wa_error')) {
      const msg = searchParams.get('wa_error');
      console.error('WA error:', msg);
      toast.error(msg, { duration: 8000 });
      setSearchParams({});
    }
  }, [searchParams, refreshUser, setSearchParams]);

  // ── Simple flow: FB Embedded Signup popup ──────────────────────────────────
  async function handleSimpleConnect() {
    setConnecting(true);
    launchEmbeddedSignup(
      async ({ code, waba_id, phone_number_id, business_id }) => {
        try {
          const r = await api.post('/facebook/embedded-signup', { code, waba_id, phone_number_id, business_id });
          await refreshUser();
          toast.success(`Connected: ${r.data.business_name || r.data.phone_number || 'WhatsApp Business'}!`);
          setConnectMode(null);
        } catch (err) {
          toast.error(err.response?.data?.error || err.response?.data?.detail || 'Connection failed', { duration: 8000 });
        }
        setConnecting(false);
      },
      (errMsg) => {
        toast.error(errMsg, { duration: 8000 });
        setConnecting(false);
      }
    );
  }

  // ── Advanced flow: OAuth redirect to save token ────────────────────────────
  function startAdvancedOAuth() {
    const token = localStorage.getItem('token');
    window.location.href = `/api/facebook/connect?token=${encodeURIComponent(token)}`;
  }

  // ── Save WABA manually (advanced) ─────────────────────────────────────────
  // Step 1: Verify WABA ID → get phone numbers under it
  async function verifyWabaId() {
    if (!wabaIdInput.trim()) return toast.error('Enter your WABA ID first');
    setVerifying(true);
    setVerifiedWaba(null);
    try {
      const r = await api.post('/facebook/verify-waba', { waba_id: wabaIdInput.trim() });
      setVerifiedWaba(r.data);
      toast.success(`Found ${r.data.phones.length} phone number(s) — select one below`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verification failed');
    }
    setVerifying(false);
  }

  // Step 2: Save the chosen phone number
  async function saveVerifiedPhone(phone) {
    setSavingWaba(true);
    try {
      await api.post('/facebook/save-waba', {
        waba_id:         verifiedWaba.waba_id,
        phone_number_id: phone.id,
        business_name:   verifiedWaba.waba_name || phone.verified_name || '',
        phone_number:    phone.display_phone_number || '',
      });
      await refreshUser();
      setVerifiedWaba(null);
      setWabaIdInput('');
      setConnectMode(null);
      toast.success('WhatsApp Business Account connected!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
    setSavingWaba(false);
  }

  // For edit-while-connected
  async function saveWabaManually(e) {
    e.preventDefault();
    setSavingWaba(true);
    try {
      await api.post('/facebook/save-waba', wabaForm);
      await refreshUser();
      setConnectMode(null);
      toast.success('Updated!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
    setSavingWaba(false);
  }

  async function disconnectWA() {
    if (!window.confirm('Disconnect WhatsApp? You can reconnect at any time.')) return;
    await api.delete('/auth/wa-disconnect');
    await refreshUser();
    toast.success('Disconnected');
  }

  async function saveEmailConfig(e) {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await api.put('/auth/email-config', emailForm);
      await refreshUser();
      toast.success('Email settings saved');
    } catch { toast.error('Failed to save'); }
    setSavingEmail(false);
  }

  // Verify DKIM/DMARC/SPF DNS records for the sending domain
  async function verifyEmailDns() {
    const domain = (emailForm.dkim_enabled && emailForm.dkim_domain_name) ||
      (emailForm.from_address || '').split('@')[1] || '';
    if (!domain.trim()) return toast.error('Set a From Email Address (or DKIM domain) first');
    setVerifyingDns(true);
    setDnsResults(null);
    try {
      const r = await api.post('/auth/email-config/verify-dns', {
        domain: domain.trim(),
        selector: emailForm.dkim_enabled ? emailForm.dkim_key_selector : '',
        dkim_private_key: emailForm.dkim_enabled ? emailForm.dkim_private_key : '',
      });
      setDnsResults(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'DNS check failed');
    }
    setVerifyingDns(false);
  }

  async function saveTag(e) {
    e.preventDefault();
    try {
      if (editingTag) {
        const r = await api.put(`/tags/${editingTag._id}`, tagForm);
        setTags(ts => ts.map(t => t._id === editingTag._id ? r.data : t));
        toast.success('Tag updated');
      } else {
        const r = await api.post('/tags', tagForm);
        setTags(ts => [...ts, r.data]);
        toast.success('Tag created');
      }
      setEditingTag(null);
      setTagForm({ name: '', color: '#6366f1', description: '' });
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  }

  async function deleteTag(id) {
    if (!window.confirm('Delete tag and remove it from all contacts?')) return;
    await api.delete(`/tags/${id}`);
    setTags(ts => ts.filter(t => t._id !== id));
    toast.success('Deleted');
  }

  const webhookUrl = `${window.location.origin}/api/webhooks/whatsapp`;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Signed in as <strong style={{ color: 'var(--text)' }}>{user?.name}</strong> · {user?.email}
        </span>
      </div>

      {/* ══ WhatsApp WABA ══════════════════════════════════════════════════════ */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">📱 WhatsApp Business Account</div>

        {/* ── CONNECTED ─────────────────────────────────── */}
        {isConnected && connectMode === null && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 16, background: 'rgba(34,197,94,0.08)', border: '1px solid var(--success)', borderRadius: 10, marginBottom: 16 }}>

              <span style={{ fontSize: 32 }}>✅</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{wa.business_name || 'Connected'}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  📞 {wa.phone_number || '—'}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2 }}>
                  Phone ID: <code>{wa.phone_number_id}</code> &nbsp;·&nbsp; WABA ID: <code>{wa.waba_id}</code>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setConnectMode('advanced'); setWabaForm({ waba_id: wa.waba_id || '', phone_number_id: wa.phone_number_id || '', business_name: wa.business_name || '', phone_number: wa.phone_number || '' }); }}>
                ✏️ Edit Details
              </button>
              {quickConnectAvailable && (
                <button className="btn btn-ghost btn-sm" onClick={handleSimpleConnect} disabled={connecting || !sdkReady}>
                  🔄 Re-connect
                </button>
              )}
              <button className="btn btn-danger btn-sm" onClick={disconnectWA}>Disconnect</button>
            </div>

          </div>
        )}

        {/* ── NOT CONNECTED — choose a method ──────────── */}
        {!isConnected && connectMode === null && !tokenSaved && (
          <div>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20, lineHeight: 1.7 }}>
              Choose how you want to connect your WhatsApp Business Account:
            </p>
            {quickConnectEnabled && !signupConfig.loading && !signupConfig.configured && (
              <div style={{ padding: 12, marginBottom: 16, borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid var(--warning)', color: 'var(--text-muted)', fontSize: 13 }}>
                {signupConfig.error || (
                  <>
                    Embedded Signup v4 is not configured in the running server process.
                    {signupConfig.missing_environment_variables?.length > 0 && (
                      <> Missing: <code>{signupConfig.missing_environment_variables.join(', ')}</code>.</>
                    )}
                    {' '}Restart the service after updating its environment, or use Manual Setup.
                  </>
                )}
              </div>
            )}
            {quickConnectAvailable && signupConfig.recovery_configured === false && (
              <div style={{ padding: 12, marginBottom: 16, borderRadius: 8, background: 'rgba(245,158,11,0.08)', border: '1px solid var(--warning)', color: 'var(--text-muted)', fontSize: 13 }}>
                Quick Connect recovery is not configured. Set <code>META_SYSTEM_USER_ACCESS_TOKEN</code> in the server environment and restart the service so signup can finish when Meta omits its browser completion event.
              </div>
            )}
            {sdkError && (
              <div style={{ padding: 12, marginBottom: 16, borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid var(--danger)', color: 'var(--danger)', fontSize: 13 }}>
                {sdkError}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: quickConnectAvailable ? '1fr 1fr' : '1fr', gap: 16 }}>

              {/* Option 1 — Simple */}
              {quickConnectAvailable && (
              <div className="connect-option" onClick={!connecting && sdkReady ? handleSimpleConnect : undefined}
                style={{ opacity: connecting || !sdkReady ? 0.6 : 1, cursor: connecting || !sdkReady ? 'not-allowed' : 'pointer' }}>
                <div className="connect-option-icon">⚡</div>
                <div className="connect-option-title">Quick Connect</div>
                <div className="connect-option-desc">
                  Meta guides you through verifying a phone number and selecting or creating your WhatsApp Business Account. No IDs needed.
                </div>
                <button
                  className="btn btn-primary"
                  style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}
                  disabled={connecting || !sdkReady}
                  onClick={e => { e.stopPropagation(); handleSimpleConnect(); }}
                >
                  {connecting ? 'Opening Facebook…' : !sdkReady ? 'Loading…' : '⚡ Quick Connect'}
                </button>
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                  Recommended for most users
                </div>
              </div>
              )}

              {/* Option 2 — Advanced */}
              <div className="connect-option" onClick={() => setConnectMode('advanced')}
                style={{ cursor: 'pointer' }}>
                <div className="connect-option-icon">🔧</div>
                <div className="connect-option-title">Manual Setup</div>
                <div className="connect-option-desc">
                  Already know your WABA ID and Phone Number ID? Authorize with Facebook then enter them directly.
                </div>
                <button
                  className="btn btn-ghost"
                  style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}
                  onClick={e => { e.stopPropagation(); setConnectMode('advanced'); }}
                >
                  🔧 Manual Setup
                </button>
                <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>
                  For developers &amp; advanced users
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ADVANCED MANUAL FORM ─────────────────────── */}
        {(connectMode === 'advanced' || tokenSaved) && !isConnected && (
          <div>
            {/* Step 1 — Authorize */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: tokenSaved ? 'var(--success)' : 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                {tokenSaved ? '✓' : '1'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Authorize with Facebook</div>
                {tokenSaved
                  ? <div style={{ color: 'var(--success)', fontSize: 13 }}>✅ Facebook authorized successfully</div>
                  : <>
                      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 10 }}>Grant this app access to your WhatsApp Business Account.</p>
                      <button className="btn btn-primary" onClick={startAdvancedOAuth}>
                        <span style={{ fontWeight: 700 }}>f</span>&nbsp; Authorize with Facebook
                      </button>
                    </>
                }
              </div>
            </div>

            {/* Step 2 — Enter WABA ID and verify */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20, opacity: tokenSaved ? 1 : 0.45 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: verifiedWaba ? 'var(--success)' : 'var(--surface2)', border: '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                {verifiedWaba ? '✓' : '2'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>Enter your WABA ID</div>

                {/* Visual guide */}
                <div style={{ background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 12, lineHeight: 2, color: 'var(--text-muted)' }}>
                  <strong style={{ color: 'var(--primary-light)' }}>📍 How to find your WABA ID:</strong><br />
                  1. Open <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-light)' }}>business.facebook.com</a><br />
                  2. Click <strong style={{ color: 'var(--text)' }}>Settings</strong> (bottom-left gear icon)<br />
                  3. Click <strong style={{ color: 'var(--text)' }}>WhatsApp accounts</strong> in the left menu<br />
                  4. Click your account name → copy the <strong style={{ color: 'var(--text)' }}>WhatsApp Business Account ID</strong>
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: verifiedWaba ? 14 : 0 }}>
                  <input
                    className="input"
                    placeholder="e.g. 123456789012345"
                    value={wabaIdInput}
                    onChange={e => { setWabaIdInput(e.target.value); setVerifiedWaba(null); }}
                    disabled={!tokenSaved}
                    onKeyDown={e => e.key === 'Enter' && verifyWabaId()}
                    style={{ fontFamily: 'monospace' }}
                  />
                  <button className="btn btn-primary" onClick={verifyWabaId} disabled={verifying || !tokenSaved}>
                    {verifying ? 'Checking…' : 'Verify →'}
                  </button>
                </div>

                {/* Phone numbers returned after WABA verified */}
                {verifiedWaba && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 13, color: 'var(--success)', fontWeight: 600, marginBottom: 10 }}>
                      ✅ WABA verified: <span style={{ color: 'var(--text)' }}>{verifiedWaba.waba_name || verifiedWaba.waba_id}</span>
                      &nbsp;— select your phone number:
                    </div>
                    {verifiedWaba.phones.map(p => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: 'var(--surface2)', borderRadius: 8, marginBottom: 8, border: '1px solid var(--border)' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 15 }}>📞 {p.display_phone_number}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                            {p.verified_name} &nbsp;·&nbsp;
                            <span style={{ color: p.code_verification_status === 'VERIFIED' ? 'var(--success)' : 'var(--warning)' }}>
                              {p.code_verification_status}
                            </span>
                          </div>
                        </div>
                        <button className="btn btn-success" disabled={savingWaba} onClick={() => saveVerifiedPhone(p)}>
                          {savingWaba ? 'Saving…' : 'Use this number →'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConnectMode(null)}>← Back</button>
          </div>
        )}

        {/* ── CONNECTED + show edit form ────────────────── */}
        {isConnected && connectMode === 'advanced' && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Update WABA Details</div>
            <form onSubmit={saveWabaManually}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div className="form-group">
                  <label className="label">WABA ID *</label>
                  <input className="input" required value={wabaForm.waba_id}
                    onChange={e => setWabaForm(f => ({ ...f, waba_id: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="label">Phone Number ID *</label>
                  <input className="input" required value={wabaForm.phone_number_id}
                    onChange={e => setWabaForm(f => ({ ...f, phone_number_id: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="label">Business Name</label>
                  <input className="input" value={wabaForm.business_name}
                    onChange={e => setWabaForm(f => ({ ...f, business_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="label">Display Phone Number</label>
                  <input className="input" value={wabaForm.phone_number}
                    onChange={e => setWabaForm(f => ({ ...f, phone_number: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn btn-primary" disabled={savingWaba}>{savingWaba ? 'Saving…' : 'Save Changes'}</button>
                <button type="button" className="btn btn-ghost" onClick={() => setConnectMode(null)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Webhook info — always visible */}
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 500, marginBottom: 6, fontSize: 13 }}>
            Webhook URL <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(register in Meta Dashboard → WhatsApp → Configuration)</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input" readOnly value={webhookUrl} style={{ fontFamily: 'monospace', fontSize: 12 }} />
            <button className="btn btn-ghost btn-sm" onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('Copied!'); }}>Copy</button>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 6 }}>
            Subscribe to <strong>messages</strong> field. Verify token:&nbsp;
            <code style={{ background: 'var(--surface2)', padding: '1px 5px', borderRadius: 4 }}>my_verify_token</code>
          </p>
        </div>
      </div>

      {/* ══ Team — inbox agent seats (owners only) ═══════════════════════════ */}
      {user?.team_role !== 'agent' && <TeamCard />}

      {/* ══ Public API access (owners only) ═══════════════════════════════════ */}
      {user?.team_role !== 'agent' && <ApiAccessCard />}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>

        {/* Email config */}
        <div className="card">
          <div className="card-title">📧 Email / SMTP Settings</div>
          <form onSubmit={saveEmailConfig}>
            {[
              { key: 'smtp_host',     label: 'SMTP Host',               placeholder: 'smtp.gmail.com' },
              { key: 'smtp_port',     label: 'SMTP Port',               placeholder: '587', type: 'number' },
              { key: 'smtp_user',     label: 'Username / Email',        placeholder: 'you@gmail.com' },
              { key: 'smtp_pass',     label: 'Password / App Password', placeholder: '••••••••', type: 'password' },
              { key: 'from_name',     label: 'From Name',               placeholder: 'Your Business' },
              { key: 'from_address',  label: 'From Email Address',      placeholder: 'noreply@yourdomain.com' },
            ].map(({ key, label, placeholder, type }) => (
              <div className="form-group" key={key}>
                <label className="label">{label}</label>
                <input className="input" type={type || 'text'} placeholder={placeholder}
                  value={emailForm[key] || ''}
                  onChange={e => setEmailForm(f => ({ ...f, [key]: type === 'number' ? parseInt(e.target.value) : e.target.value }))} />
              </div>
            ))}
            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 12 }}>
              Gmail: generate an App Password at Google Account → Security → 2-Step Verification → App Passwords.
            </p>

            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginBottom: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                <input type="checkbox" checked={!!emailForm.dkim_enabled}
                  onChange={e => setEmailForm(f => ({ ...f, dkim_enabled: e.target.checked }))} />
                Enable DKIM signing (optional)
              </label>
              <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '6px 0 12px' }}>
                Only needed if your SMTP relay doesn't already sign outbound mail (e.g. a self-hosted server on your
                own domain). Requires a DKIM key pair — publish the public key as a TXT record at{' '}
                <code>&lt;selector&gt;._domainkey.&lt;yourdomain&gt;</code> and paste the private key below.
              </p>

              {emailForm.dkim_enabled && (
                <>
                  <div className="form-group">
                    <label className="label">Signing Domain</label>
                    <input className="input" placeholder="yourdomain.com"
                      value={emailForm.dkim_domain_name || ''}
                      onChange={e => setEmailForm(f => ({ ...f, dkim_domain_name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="label">DKIM Selector</label>
                    <input className="input" placeholder="default"
                      value={emailForm.dkim_key_selector || ''}
                      onChange={e => setEmailForm(f => ({ ...f, dkim_key_selector: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label className="label">DKIM Private Key (PEM)</label>
                    <textarea className="textarea" rows={5} placeholder="-----BEGIN PRIVATE KEY-----"
                      style={{ fontFamily: 'monospace', fontSize: 12 }}
                      value={emailForm.dkim_private_key || ''}
                      onChange={e => setEmailForm(f => ({ ...f, dkim_private_key: e.target.value }))} />
                  </div>
                </>
              )}
            </div>

            <button type="submit" className="btn btn-primary" disabled={savingEmail}>
              {savingEmail ? 'Saving…' : 'Save Email Settings'}
            </button>
          </form>

          {/* DNS authentication check (DKIM / DMARC / SPF) */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>🔐 Domain Authentication</div>
            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 12 }}>
              Checks your sending domain's DNS for DKIM, DMARC and SPF records — these prevent
              spoofing and keep your campaigns out of spam folders.
            </p>
            <button type="button" className="btn btn-ghost btn-sm" onClick={verifyEmailDns} disabled={verifyingDns}>
              {verifyingDns ? 'Checking DNS…' : 'Verify DNS Records'}
            </button>

            {dnsResults && (
              <div style={{ marginTop: 12 }}>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 8 }}>
                  Domain: <code>{dnsResults.domain}</code>
                  {dnsResults.selector && <> · Selector: <code>{dnsResults.selector}</code></>}
                </div>
                {[['DKIM', dnsResults.dkim], ['DMARC', dnsResults.dmarc], ['SPF', dnsResults.spf]].map(([label, r]) => {
                  const icon  = { pass: '✅', warn: '⚠️', fail: '❌', skip: '➖', error: '⚠️' }[r.status] || '❔';
                  const color = { pass: 'var(--success)', warn: 'var(--warning)', fail: 'var(--danger)', skip: 'var(--text-muted)', error: 'var(--warning)' }[r.status] || 'var(--text-muted)';
                  return (
                    <div key={label} style={{ padding: '10px 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color, marginBottom: 2 }}>{icon} {label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>{r.message}</div>
                      {r.record && (
                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)', marginTop: 6, wordBreak: 'break-all', opacity: 0.8 }}>
                          {r.record.length > 120 ? r.record.slice(0, 120) + '…' : r.record}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Tags */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-title">{editingTag ? 'Edit Tag' : 'Create Tag'}</div>
            <form onSubmit={saveTag}>
              <div className="form-group">
                <label className="label">Tag Name *</label>
                <input className="input" required value={tagForm.name}
                  onChange={e => setTagForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Harare, VIP, Newsletter" />
              </div>
              <div className="form-group">
                <label className="label">Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="color" value={tagForm.color}
                    onChange={e => setTagForm(f => ({ ...f, color: e.target.value }))}
                    style={{ width: 40, height: 36, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }} />
                  <span className="tag-chip" style={{ color: tagForm.color, borderColor: tagForm.color }}>
                    {tagForm.name || 'Preview'}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary">{editingTag ? 'Update' : 'Create Tag'}</button>
                {editingTag && (
                  <button type="button" className="btn btn-ghost"
                    onClick={() => { setEditingTag(null); setTagForm({ name: '', color: '#6366f1', description: '' }); }}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="card">
            <div className="card-title">All Tags ({tags.length})</div>
            {tags.length === 0
              ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No tags yet</div>
              : tags.map(t => (
                <div key={t._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="tag-chip" style={{ color: t.color, borderColor: t.color }}>{t.name}</span>
                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{t.contact_count} contacts</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditingTag(t); setTagForm({ name: t.name, color: t.color, description: t.description || '' }); }}>Edit</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteTag(t._id)}>Del</button>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Public API — personal Bearer key for /api/v1 (docs at /api-docs) ─────────
function ApiAccessCard() {
  const [info, setInfo] = useState(null); // { has_key, prefix, created_at, last_used_at }
  const [newKey, setNewKey] = useState(null); // full key, shown once after generation
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/settings/api-key').then(r => setInfo(r.data)).catch(() => {});
  }, []);

  async function generate() {
    if (info?.has_key && !window.confirm('Generate a new API key? Your current key stops working immediately.')) return;
    setBusy(true);
    try {
      const r = await api.post('/settings/api-key');
      setNewKey(r.data.api_key);
      setInfo({ has_key: true, prefix: r.data.prefix, created_at: r.data.created_at, last_used_at: null });
      toast.success('API key generated — copy it now, it won\'t be shown again');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to generate key'); }
    setBusy(false);
  }

  async function revoke() {
    if (!window.confirm('Revoke your API key? All API requests using it will start failing.')) return;
    setBusy(true);
    try {
      await api.delete('/settings/api-key');
      setInfo({ has_key: false, prefix: null, created_at: null, last_used_at: null });
      setNewKey(null);
      toast.success('API key revoked');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to revoke'); }
    setBusy(false);
  }

  const docsUrl = 'http://localhost:5000/api-docs';

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-title">🔌 API Access</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 14, lineHeight: 1.7 }}>
        Run your customers, conversations and campaigns from your own systems via our REST API —
        contacts, tags, inbox threads, broadcasts, tracked links and reporting. Requests are
        authenticated with your API key as a Bearer token, and anything you send shows up in your
        Inbox. Read the <a href={docsUrl} target="_blank" rel="noopener noreferrer"
        style={{ color: 'var(--primary-light)' }}>API documentation</a> for endpoints, examples
        and error codes.
      </p>

      {/* What the integration has to design around, without having to ask. */}
      {info && (
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -6, marginBottom: 14 }}>
          Your limit: <strong style={{ color: 'var(--text)' }}>
            {info.rate_limit?.requests_per_minute
              ? `${info.rate_limit.requests_per_minute} requests/minute`
              : 'no limit'}
          </strong>
          {info.rate_limit?.source === 'account' && ' (set for your account)'}
          {info.api_enabled === false && (
            <span style={{ color: 'var(--danger)' }}> · the API is currently switched off platform-wide</span>
          )}
        </p>
      )}

      {newKey && (
        <div style={{ padding: 14, background: 'rgba(34,197,94,0.08)', border: '1px solid var(--success)', borderRadius: 10, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
            ✅ Your new API key — copy it now, it won't be shown again:
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input className="input" readOnly value={newKey} style={{ fontFamily: 'monospace', fontSize: 12 }} />
            <button className="btn btn-ghost btn-sm" onClick={() => {
              navigator.clipboard.writeText(newKey);
              toast.success('Copied!');
            }}>Copy</button>
          </div>
        </div>
      )}

      {info?.has_key ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13 }}>
            <div>Key: <code style={{ background: 'var(--surface2)', padding: '2px 6px', borderRadius: 4 }}>{info.prefix}</code></div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
              Created {info.created_at ? new Date(info.created_at).toLocaleString() : '—'}
              {' · '}Last used {info.last_used_at ? new Date(info.last_used_at).toLocaleString() : 'never'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={generate} disabled={busy}>🔄 Rotate key</button>
            <button className="btn btn-danger btn-sm" onClick={revoke} disabled={busy}>Revoke</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-primary" onClick={generate} disabled={busy || !info}>
          {busy ? 'Generating…' : '🔑 Generate API key'}
        </button>
      )}
    </div>
  );
}

// ── Team management — invite and manage inbox agent seats ───────────────────
function TeamCard() {
  const [agents, setAgents] = useState([]);
  const [pages, setPages] = useState([]); // grantable page catalog, from the server
  const [form, setForm] = useState({ name: '', email: '', allowed_pages: ['inbox'] });
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    api.get('/team').then(r => setAgents(r.data)).catch(() => {});
    api.get('/team/pages').then(r => setPages(r.data)).catch(() => {});
  }, []);

  function togglePage(list, key) {
    return list.includes(key) ? list.filter(p => p !== key) : [...list, key];
  }

  async function invite(e) {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Name and email required');
    setInviting(true);
    try {
      const { data } = await api.post('/team/invite', form);
      setAgents(prev => [...prev, data.agent]);
      setForm({ name: '', email: '', allowed_pages: ['inbox'] });
      if (data.email_sent) {
        toast.success(`Invite sent to ${data.agent.email} with their temporary password`);
      } else if (data.temp_password) {
        // No system SMTP configured — surface the password once for manual sharing
        window.prompt(
          `Couldn't email the invite (configure System Email in the Admin page). Share this temporary password with ${data.agent.name} — it won't be shown again:`,
          data.temp_password
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invite failed');
    } finally {
      setInviting(false);
    }
  }

  async function toggleActive(agent) {
    try {
      const { data } = await api.patch(`/team/${agent._id}`, { isActive: !agent.isActive });
      setAgents(prev => prev.map(a => a._id === data._id ? data : a));
      toast.success(data.isActive ? 'Agent re-activated' : 'Agent deactivated — their conversations were unassigned');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    }
  }

  async function toggleAgentPage(agent, key) {
    const allowed_pages = togglePage(agent.allowed_pages || [], key);
    try {
      const { data } = await api.patch(`/team/${agent._id}`, { allowed_pages });
      setAgents(prev => prev.map(a => a._id === data._id ? data : a));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed');
    }
  }

  async function resetPassword(agent) {
    const password = window.prompt(`New password for ${agent.name} (min 6 characters):`);
    if (!password) return;
    try {
      await api.post(`/team/${agent._id}/reset-password`, { password });
      toast.success('Password updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Reset failed');
    }
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-title">👥 Team — Inbox Agents</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 14 }}>
        Agents log in with their own account and share your WhatsApp inbox. They can read and
        reply to conversations, but can't see your WhatsApp credentials or broadcasts.
        A temporary password is generated automatically and emailed to the agent.
      </p>

      {agents.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {agents.map(a => (
            <div key={a._id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {a.name}
                    {!a.isActive && <span style={{ color: 'var(--danger)', fontSize: 11, marginLeft: 8 }}>● Deactivated</span>}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{a.email}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => resetPassword(a)}>Reset password</button>
                  <button className={`btn btn-sm ${a.isActive ? 'btn-danger' : 'btn-ghost'}`} onClick={() => toggleActive(a)}>
                    {a.isActive ? 'Deactivate' : 'Re-activate'}
                  </button>
                </div>
              </div>
              {pages.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {pages.map(pg => {
                    const checked = (a.allowed_pages || []).includes(pg.key);
                    return (
                      <button
                        key={pg.key}
                        type="button"
                        onClick={() => toggleAgentPage(a, pg.key)}
                        className="btn btn-sm"
                        style={{
                          background: checked ? 'var(--primary)' : 'transparent',
                          color: checked ? '#fff' : 'var(--text-muted)',
                          border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
                        }}
                        title={checked ? `Remove ${pg.label} access` : `Grant ${pg.label} access`}
                      >{pg.label}</button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <form onSubmit={invite} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, alignItems: 'end' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="label">Name</label>
            <input className="input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Agent name" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="label">Email</label>
            <input className="input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="agent@company.com" />
          </div>
          <button className="btn btn-primary" disabled={inviting} style={{ height: 38 }}>
            {inviting ? 'Inviting…' : '＋ Add agent'}
          </button>
        </div>

        {pages.length > 0 && (
          <div>
            <label className="label">Access — pages this agent can use</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
              {pages.map(pg => {
                const checked = form.allowed_pages.includes(pg.key);
                return (
                  <button
                    key={pg.key}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, allowed_pages: togglePage(f.allowed_pages, pg.key) }))}
                    className="btn btn-sm"
                    style={{
                      background: checked ? 'var(--primary)' : 'transparent',
                      color: checked ? '#fff' : 'var(--text-muted)',
                      border: `1px solid ${checked ? 'var(--primary)' : 'var(--border)'}`,
                    }}
                  >{pg.label}</button>
                );
              })}
            </div>
          </div>
        )}
      </form>

      <AssignmentSettings />
    </div>
  );
}

// ── Chat assignment routing — how new inbox chats reach agents ───────────────
const ASSIGNMENT_MODES = [
  {
    key: 'manual', icon: '🖐', label: 'Manual',
    desc: 'No auto-routing — you and your team pick up chats from the Unassigned queue yourselves.',
  },
  {
    key: 'round_robin', icon: '⚖️', label: 'Balanced (round robin)',
    desc: 'Each new chat goes to the next team member in turn, keeping workloads even.',
  },
  {
    key: 'least_busy', icon: '📉', label: 'Least busy',
    desc: 'New chats go to whoever has the fewest open chats right now.',
  },
  {
    key: 'fastest_response', icon: '⚡', label: 'Speed (fastest responder)',
    desc: 'New chats go to the member with the best average first-reply time over the last 7 days.',
  },
  {
    key: 'sticky', icon: '🤝', label: 'Returning customer',
    desc: 'Customers are routed back to the agent who last helped them. Brand-new customers rotate evenly.',
  },
];

function AssignmentSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/inbox/settings').then(r => setSettings(r.data)).catch(() => {});
  }, []);

  async function save(updates) {
    if (saving) return;
    setSaving(true);
    const prev = settings;
    setSettings(s => ({ ...s, ...updates })); // optimistic
    try {
      const { data } = await api.put('/inbox/settings', updates);
      setSettings(data);
      if (updates.assignment_mode) {
        const mode = ASSIGNMENT_MODES.find(m => m.key === data.assignment_mode);
        toast.success(`Chat assignment: ${mode?.label || data.assignment_mode}`);
      }
    } catch (err) {
      setSettings(prev);
      toast.error(err.response?.data?.error || 'Failed to save assignment setting');
    } finally {
      setSaving(false);
    }
  }

  if (!settings) return null;

  return (
    <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>🎯 Chat assignment</div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
        Choose how new incoming chats are routed to your team. Applies from the next
        incoming message — already-assigned chats are never moved.
      </p>

      <div style={{ display: 'grid', gap: 8 }}>
        {ASSIGNMENT_MODES.map(m => {
          const active = settings.assignment_mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => save({ assignment_mode: m.key })}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'left',
                padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                background: active ? 'rgba(99,102,241,0.08)' : 'transparent',
                color: 'var(--text)',
              }}
            >
              <span style={{ fontSize: 18 }}>{m.icon}</span>
              <span>
                <span style={{ fontWeight: 600, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {m.label}
                  {active && <span style={{ color: 'var(--primary-light)', fontSize: 11 }}>● Active</span>}
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>{m.desc}</span>
              </span>
            </button>
          );
        })}
      </div>

      {settings.assignment_mode !== 'manual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.online_only}
              onChange={e => save({ online_only: e.target.checked })}
            />
            Only assign to members currently online in the inbox
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
              (when nobody is online, chats wait in the Unassigned queue)
            </span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.include_owner}
              onChange={e => save({ include_owner: e.target.checked })}
            />
            Include me (the owner) in the rotation
          </label>
        </div>
      )}

      {/* Reply priority — independent of the mode above, works even on manual */}
      <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface2)' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.reply_priority}
            onChange={e => save({ reply_priority: e.target.checked })}
            style={{ marginTop: 3 }}
          />
          <span>
            <span style={{ fontWeight: 600 }}>↩ Reply priority</span>
            <span style={{ color: 'var(--text-muted)', fontSize: 12.5, display: 'block' }}>
              When a customer answers a message an agent recently sent them, the chat jumps to the
              top of the inbox and goes straight back to that agent — it never waits at the back
              of the queue. Works with every mode, including Manual.
            </span>
          </span>
        </label>
        {settings.reply_priority && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 10, marginLeft: 24 }}>
            Treat replies as "recent" for
            <input
              className="input"
              type="number"
              min={1}
              max={168}
              value={settings.reply_priority_window_hours}
              onChange={e => setSettings(s => ({ ...s, reply_priority_window_hours: e.target.value }))}
              onBlur={e => {
                const h = Math.min(Math.max(parseInt(e.target.value) || 48, 1), 168);
                save({ reply_priority_window_hours: h });
              }}
              style={{ width: 70, padding: '4px 8px' }}
            />
            hours after the agent's message
          </label>
        )}
      </div>
    </div>
  );
}
